import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, Heart, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, readTime, bookmarks } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Badge } from "../components/Pill";

const CATS = ["All", "Editorial", "Haute Couture", "Real Weddings", "Bespoke Decor", "Destination Guides", "Celebrity Spotlights"];

export default function BlogList() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [blogs, setBlogs] = useState(null);
  const [saved, setSaved] = useState(() => bookmarks.get());
  const [search, setSearch] = useState(params.get("q") || "");

  useEffect(() => {
    setBlogs(null);
    const t = setTimeout(() => {
      const q = new URLSearchParams();
      if (category !== "All") q.set("category", category);
      if (search.trim()) q.set("search", search.trim());
      api(`/blogs?${q.toString()}`)
        .then(setBlogs)
        .catch(() => setBlogs([]));
    }, search.trim() ? 350 : 0);
    return () => clearTimeout(t);
  }, [category, search]);

  const toggleSave = (e, slug) => {
    e.preventDefault();
    const next = bookmarks.toggle(slug);
    setSaved([...next]);
    toast.success(next.includes(slug) ? "Saved to your reading list" : "Removed from your reading list");
  };

  return (
    <main data-testid="blog-list-page">
      <section className="bg-blush pt-36 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <Reveal><Badge tone="ink">The Brides of India Edit</Badge></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.98]">
              Wedding stories worth remembering
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-xl mx-auto text-neutral-600">
              Trend reports, real weddings, style guides and conversations from our editors in Mumbai.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 max-w-md mx-auto relative">
              <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                data-testid="blog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stories, authors, places"
                className="w-full rounded-full border-2 border-ink/10 bg-white pl-12 pr-12 py-3.5 text-sm outline-none focus:border-gold transition-colors duration-300"
              />
              {search && (
                <button
                  data-testid="blog-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-blush flex items-center justify-center text-ink/60 hover:bg-petal transition-colors duration-300"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="-mx-6 px-6 lg:mx-0 lg:px-0 overflow-x-auto no-scrollbar mb-4" data-testid="category-filters">
          <div className="flex lg:flex-wrap gap-2.5 lg:gap-3 w-max lg:w-auto">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setParams(c === "All" ? {} : { category: c })}
                className={`whitespace-nowrap rounded-full px-4 lg:px-5 py-2 lg:py-2.5 text-[9px] lg:text-[10px] uppercase tracking-[0.18em] font-bold border-2 transition-colors duration-300 ${
                  category === c
                    ? "bg-ink text-cream border-ink"
                    : "border-ink/15 text-ink/60 hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {search.trim() && blogs && (
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50 mb-8" data-testid="search-results-count">
            {blogs.length} {blogs.length === 1 ? "story" : "stories"} found for "{search.trim()}"
          </p>
        )}

        {!blogs ? (
          <p className="font-elegant italic text-2xl text-gold animate-pulse mt-8">Gathering stories</p>
        ) : blogs.length === 0 ? (
          <p className="text-neutral-500 mt-8" data-testid="blog-empty-state">
            {search.trim() ? `Nothing filed under "${search.trim()}" yet. Try another word.` : "No stories filed under this desk yet."}
          </p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 mt-8">
            {blogs.map((b, i) => (
              <Reveal key={b.id} delay={0.05 * (i % 3)}>
                <Link to={`/edit/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block">
                  <div className="relative overflow-hidden rounded-[1.75rem]">
                    <img
                      src={b.cover_image}
                      alt={b.title}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    <button
                      onClick={(e) => toggleSave(e, b.slug)}
                      data-testid={`blog-save-${b.slug}`}
                      aria-label="Save story"
                      className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur transition-colors duration-300 ${
                        saved.includes(b.slug) ? "bg-rose text-ink" : "bg-white/85 text-ink hover:bg-blush"
                      }`}
                    >
                      <Heart size={16} fill={saved.includes(b.slug) ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-2.5">
                    <Badge tone="ink">{b.category}</Badge>
                    <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-ink/50">
                      <Clock size={11} /> {readTime(b.content)} min read
                    </span>
                  </div>
                  <h2 className="mt-3 font-display uppercase text-2xl md:text-[1.7rem] text-ink leading-tight group-hover:text-gold transition-colors duration-300">
                    {b.title}
                  </h2>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed line-clamp-2">{b.excerpt}</p>
                  <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/50">
                    Words by {b.author} · {fmtDate(b.created_at)}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
